import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSequenceHotkey, useHotkey } from '../hooks/useHotkeys';

interface HotkeysContextType {
  isCommandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  isHelpDialogOpen: boolean;
  openHelpDialog: () => void;
  closeHelpDialog: () => void;
  hotkeysEnabled: boolean;
  setHotkeysEnabled: (enabled: boolean) => void;
}

const HotkeysContext = createContext<HotkeysContextType | null>(null);

export const useHotkeys = () => {
  const context = useContext(HotkeysContext);
  if (!context) {
    throw new Error('useHotkeys must be used within HotkeysProvider');
  }
  return context;
};

// Hotkey definitions for display
export interface HotkeyDefinition {
  keys: string;
  description: string;
  category: string;
}

export const hotkeyDefinitions: HotkeyDefinition[] = [
  // Navigation
  { keys: 'g then h', description: 'Go to Home', category: 'Navigation' },
  { keys: 'g then c', description: 'Go to Calendar', category: 'Navigation' },
  { keys: 'g then m', description: 'Go to Messages', category: 'Navigation' },
  { keys: 'g then s', description: 'Go to School', category: 'Navigation' },
  { keys: 'g then e', description: 'Go to Health', category: 'Navigation' },
  { keys: 'g then a', description: 'Go to Activities', category: 'Navigation' },
  { keys: 'g then o', description: 'Go to Contacts', category: 'Navigation' },
  { keys: 'g then $', description: 'Go to Money', category: 'Navigation' },
  { keys: 'g then d', description: 'Go to Discipline', category: 'Navigation' },
  { keys: 'g then t', description: 'Go to Tasks', category: 'Navigation' },
  { keys: 'g then p', description: 'Go to Scrapbook', category: 'Navigation' },
  { keys: 'g then ,', description: 'Go to Settings', category: 'Navigation' },

  // Global
  { keys: '⌘/Ctrl + K', description: 'Open command palette', category: 'Global' },
  { keys: '/', description: 'Focus search', category: 'Global' },
  { keys: '?', description: 'Show keyboard shortcuts', category: 'Global' },
  { keys: 'Escape', description: 'Close dialog/modal', category: 'Global' },

  // Calendar
  { keys: 'n', description: 'New item (context-aware)', category: 'Actions' },
  { keys: 'j', description: 'Next item/day', category: 'Actions' },
  { keys: 'k', description: 'Previous item/day', category: 'Actions' },
  { keys: 't', description: 'Go to today (in Calendar)', category: 'Actions' },
];

interface HotkeysProviderProps {
  children: React.ReactNode;
}

export const HotkeysProvider: React.FC<HotkeysProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [hotkeysEnabled, setHotkeysEnabled] = useState(true);

  const openCommandPalette = useCallback(() => setIsCommandPaletteOpen(true), []);
  const closeCommandPalette = useCallback(() => setIsCommandPaletteOpen(false), []);
  const openHelpDialog = useCallback(() => setIsHelpDialogOpen(true), []);
  const closeHelpDialog = useCallback(() => setIsHelpDialogOpen(false), []);

  // Disable hotkeys when dialogs are open
  const effectiveEnabled = hotkeysEnabled && !isCommandPaletteOpen && !isHelpDialogOpen;

  // Command palette: Cmd/Ctrl + K
  useHotkey(['meta+k', 'ctrl+k'], () => {
    setIsCommandPaletteOpen(true);
  }, { enabled: hotkeysEnabled });

  // Help dialog: ?
  useHotkey('shift+/', () => {
    setIsHelpDialogOpen(true);
  }, { enabled: effectiveEnabled });

  // Escape to close dialogs
  useHotkey('escape', () => {
    if (isCommandPaletteOpen) {
      setIsCommandPaletteOpen(false);
    } else if (isHelpDialogOpen) {
      setIsHelpDialogOpen(false);
    }
  }, { enabled: isCommandPaletteOpen || isHelpDialogOpen, enableOnFormElements: true });

  // Navigation sequences (g + letter)
  useSequenceHotkey(['g', 'h'], () => navigate('/'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 'c'], () => navigate('/calendar'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 'm'], () => navigate('/messages'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 's'], () => navigate('/school'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 'e'], () => navigate('/health'), { enabled: effectiveEnabled }); // 'e' for hEalth
  useSequenceHotkey(['g', 'a'], () => navigate('/activities'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 'o'], () => navigate('/contacts'), { enabled: effectiveEnabled }); // 'o' for cOntacts
  useSequenceHotkey(['g', 'shift+4'], () => navigate('/money'), { enabled: effectiveEnabled }); // $ for money
  useSequenceHotkey(['g', 'd'], () => navigate('/discipline'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 't'], () => navigate('/tasks'), { enabled: effectiveEnabled });
  useSequenceHotkey(['g', 'p'], () => navigate('/memories'), { enabled: effectiveEnabled }); // 'p' for Photos/memories
  useSequenceHotkey(['g', ','], () => navigate('/settings'), { enabled: effectiveEnabled }); // comma for settings

  // Focus search with /
  useHotkey('/', () => {
    const searchInput = document.querySelector<HTMLInputElement>('[data-search-input]');
    if (searchInput) {
      searchInput.focus();
    }
  }, { enabled: effectiveEnabled });

  return (
    <HotkeysContext.Provider
      value={{
        isCommandPaletteOpen,
        openCommandPalette,
        closeCommandPalette,
        isHelpDialogOpen,
        openHelpDialog,
        closeHelpDialog,
        hotkeysEnabled,
        setHotkeysEnabled,
      }}
    >
      {children}
    </HotkeysContext.Provider>
  );
};

export default HotkeysContext;
